import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import * as LucideIcons from 'lucide-react';
import * as FaIcons from 'react-icons/fa';
import * as MdIcons from 'react-icons/md';

// Evita duplicatas: Lucide exporta Home, HomeIcon, LucideHome (mesmo ícone). Mantém apenas o nome "limpo".
const lucideIconMap = new Map();
Object.keys(LucideIcons).forEach((name) => {
  if (name === 'createLucideIcon' || name === 'default' || name === 'Icon' || !/^[A-Z]/.test(name)) return;
  const comp = LucideIcons[name];
  if (!comp || (typeof comp !== 'function' && typeof comp !== 'object')) return;
  const existing = lucideIconMap.get(comp);
  const prefer = !name.startsWith('Lucide') && !name.endsWith('Icon');
  if (!existing || (prefer && (existing.startsWith('Lucide') || existing.endsWith('Icon')))) {
    lucideIconMap.set(comp, name);
  }
});
const validIconNames = [...new Set(lucideIconMap.values())];

const toKebab = (s) => s.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '');
const faIconNames = Object.keys(FaIcons).filter((n) => /^Fa[A-Z]/.test(n));
const mdIconNames = Object.keys(MdIcons).filter((n) => /^Md[A-Z]/.test(n));

class IconErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch() {}
  render() {
    return this.state.hasError ? <span style={{ fontSize: 14, color: '#94a3b8' }}>?</span> : this.props.children;
  }
}

/** Resolve ícone por nome (Lucide, fa:, md:) — mesmo critério da Lista de ícones e do App. */
export function getIconComponent(iconName) {
  if (!iconName || iconName === 'default') return null;
  const ic = String(iconName);
  if (ic.startsWith('fa:')) {
    const pascal = ic.slice(3).split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');
    return FaIcons[pascal] || null;
  }
  if (ic.startsWith('md:')) {
    const pascal = ic.slice(3).split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join('');
    return MdIcons[pascal] || null;
  }
  return LucideIcons[ic] || null;
}

/** Seletor de ícones (Lucide + FA + MD), igual ao usado em Lista de ícones / timeline / etc. */
export function IconPickerModal({ onSelect, onClose, t }) {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('lucide');

  const lucideFiltered = useMemo(
    () => validIconNames.filter((name) => name.toLowerCase().includes(search.toLowerCase())),
    [search]
  );
  const faFiltered = useMemo(
    () => faIconNames.filter((name) => toKebab(name).includes(search.toLowerCase())),
    [search]
  );
  const mdFiltered = useMemo(
    () => mdIconNames.filter((name) => toKebab(name).includes(search.toLowerCase())),
    [search]
  );

  const renderIcon = (id, IconComponent, size = 24) => {
    if (!IconComponent || (typeof IconComponent !== 'function' && typeof IconComponent !== 'object')) {
      return null;
    }
    return (
      <button
        key={id}
        type="button"
        onClick={() => onSelect(id)}
        style={{
          padding: '0.75rem',
          borderRadius: '6px',
          border: '1px solid #e2e8f0',
          backgroundColor: '#fff',
          color: '#334155',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.borderColor = '#f97316';
          e.currentTarget.style.color = '#ea580c';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.borderColor = '#e2e8f0';
          e.currentTarget.style.color = '#334155';
        }}
        title={id}
      >
        <IconErrorBoundary>
          <IconComponent size={size} />
        </IconErrorBoundary>
      </button>
    );
  };

  const content = (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2147483648,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        pointerEvents: 'auto',
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        style={{
          backgroundColor: '#fff',
          padding: '1.5rem',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '640px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
          border: '1px solid #e2e8f0',
          pointerEvents: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem', alignItems: 'center' }}>
          <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.25rem' }}>{t ? t('builder:searchIcon') : 'Procurar Ícone'}</h3>
          <button type="button" onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#64748b', fontSize: '1.5rem', lineHeight: 1, cursor: 'pointer' }}>
            &times;
          </button>
        </div>
        <div style={{ display: 'flex', gap: '4px', marginBottom: '1rem' }}>
          {['lucide', 'fa', 'md'].map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                background: activeTab === tab ? '#3b82f6' : '#f8fafc',
                color: activeTab === tab ? '#fff' : '#64748b',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: 500,
              }}
            >
              {tab === 'lucide' && (t ? t('builder:iconTabLucide') : 'Lucide')}
              {tab === 'fa' && (t ? t('builder:iconTabFontAwesome') : 'Font Awesome')}
              {tab === 'md' && (t ? t('builder:iconTabMaterial') : 'Material Icons')}
            </button>
          ))}
        </div>
        <div style={{ position: 'relative', marginBottom: '1rem' }}>
          <LucideIcons.Search size={18} style={{ position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' }} />
          <input
            autoFocus
            placeholder={t ? t('builder:filterPlaceholder') : 'Digite para filtrar...'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 1rem 0.75rem 2.5rem',
              borderRadius: '6px',
              border: '1px solid #cbd5e1',
              backgroundColor: '#f8fafc',
              color: '#334155',
              fontSize: '1rem',
            }}
          />
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(56px, 1fr))',
            gap: '0.5rem',
            overflowY: 'auto',
            flex: 1,
            paddingRight: '0.5rem',
            minHeight: '200px',
          }}
        >
          <button
            type="button"
            onClick={() => onSelect('default')}
            style={{
              padding: '0.75rem',
              borderRadius: '4px',
              border: '1px dashed #cbd5e1',
              backgroundColor: '#f8fafc',
              color: '#64748b',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.25rem',
              cursor: 'pointer',
              gridColumn: '1 / -1',
              marginBottom: '0.5rem',
              fontSize: '0.85rem',
            }}
          >
            <span>{t ? t('builder:themeAuto') : 'Padrão do Tema Automático'}</span>
          </button>
          {activeTab === 'lucide' && (lucideFiltered || []).map((name) => {
            const Comp = LucideIcons[name];
            return Comp ? renderIcon(name, Comp) : null;
          })}
          {activeTab === 'fa' &&
            (faFiltered || []).map((name) => {
              const Comp = FaIcons[name];
              return Comp ? renderIcon(`fa:${toKebab(name)}`, Comp) : null;
            })}
          {activeTab === 'md' &&
            (mdFiltered || []).map((name) => {
              const Comp = MdIcons[name];
              return Comp ? renderIcon(`md:${toKebab(name)}`, Comp) : null;
            })}
        </div>
      </div>
    </div>
  );
  return createPortal(content, document.body);
}
